/*
 * JAG Find in Store — correct the per-location availability LABEL (#984)
 * ------------------------------------------------------------------
 * Find in Store is rendered by the "Multi Location Inventory Info" app. Its
 * drawer shows an availability line per store. In the JAG design that line is
 * restyled by jag.css: the app's own status text is hidden and replaced with a
 * dot + "Available in store" (green) or "Out of stock" (pink).
 *
 * The ORIGINAL relabel keyed "Out of stock" off the app's Notify Me button
 * (`.iia-drawer .iia-list-item:has(.iia-notify-button)`). That button is only
 * injected for the app's PER-STORE notify feature, which JAG does not use
 * (JAG's Notify Me is the variant-level STOQ form). So the button never renders,
 * the "Out of stock" flip never fires, and EVERY listed store fell through to
 * the "Available in store" default — even ones the app itself flags "Sold out".
 * That is the reported bug: stores shown "available" holding none.
 *
 * Fix: classify each drawer row from the app's OWN per-location status, which it
 * already renders into `.iia-stock-threshold` (e.g. "Sold out" / "Last few" /
 * "In stock") for the CURRENT variant. Rows whose status is the app's sold-out
 * message get `.jag-fis-sold-out`; jag.css maps that class to "Out of stock" +
 * a pink dot (replacing the dead `:has()` selector).
 *
 * Why the rendered text, not the raw quantity: on variant change the app
 * re-renders the drawer with the new variant's status text, but it does NOT
 * update `window.inventoryInfo.product.selectedVairant` (verified live — it
 * stays on the initial variant), and the storefront/app variant ids live in
 * different id spaces. Reading the app's rendered status sidesteps both: it is
 * always correct for whatever variant is on screen. Which status counts as
 * sold out is read from the app's own `stockMessageRules` — see
 * soldOutThresholds() for how, and for the trade it makes.
 *
 * Not a web component (project rule) because there is no element to mount — the
 * app owns the drawer markup; this observes it and annotates the rows.
 */
(function () {
  const SOLD_OUT_CLASS = 'jag-fis-sold-out';
  // Fallback only, when the app's rules can't be read — never a false "available".
  const SOLD_OUT_FALLBACK = /(sold\s*out|out of stock|no stock|unavailable)/i;

  const raf =
    (typeof window !== 'undefined' && window.requestAnimationFrame) ||
    function (cb) {
      return setTimeout(cb, 16);
    };

  /* The app's own sold-out status string(s), lower-cased for comparison.
     Two INDEPENDENT signals, unioned:

       1. STRUCTURAL — the `<=` rule with the lowest qty is the app's "none
          left" band, whatever it is called. This is what survives a merchant
          renaming the status to wording no regex anticipates ("None left",
          "Gone", "Nil"). Selecting the rule by wording could not: the same
          regex decided which rule WAS the sold-out rule and then decided
          whether the rendered text matched it, so an unanticipated rename
          missed twice and every sold-out store silently read "Available in
          store" — the exact bug #984 exists to fix.
       2. WORDING — any rule whose threshold reads as sold out. Keeps working
          if the rules are not ordered the way (1) assumes.

     Union rather than either/or, and deliberately biased toward sold-out:
     this file exists because rows holding nothing were labelled available,
     and sending a shopper to a store with no stock is the costlier error.

     ⚠️ The trade, stated rather than hidden: a config with NO sold-out band
     at all (say `<= 2 "Almost gone"` as its lowest rule) has that lowest band
     read as sold out. That is the safe direction for a stock-accuracy bug,
     but it is a real behaviour, so it belongs in the comment. On JAG's live
     rules both signals agree on "Sold out" and nothing changes. */
  function soldOutThresholds() {
    const info = window.inventoryInfo;
    const settings = info && info.settings;
    const rules =
      settings && Array.isArray(settings.stockMessageRules)
        ? settings.stockMessageRules
        : [];

    const out = [];
    function add(value) {
      if (!value) return;
      const text = String(value).trim().toLowerCase();
      if (text && out.indexOf(text) === -1) out.push(text);
    }

    // 1. Structural: lowest-qty `<=` rule.
    let lowest = null;
    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i];
      if (!rule || rule.operator !== '<=') continue;
      const qty = Number(rule.qty);
      if (!Number.isFinite(qty)) continue;
      if (lowest === null || qty < lowest.qty) {
        lowest = { qty: qty, threshold: rule.threshold };
      }
    }
    if (lowest) add(lowest.threshold);

    // 2. Wording.
    for (let i = 0; i < rules.length; i++) {
      const threshold = rules[i] && rules[i].threshold;
      if (threshold && SOLD_OUT_FALLBACK.test(String(threshold))) add(threshold);
    }

    return out;
  }

  function isSoldOut(li) {
    const el = li.querySelector('.iia-stock-threshold');
    if (!el) return false;
    const text = (el.textContent || '').trim().toLowerCase();
    if (!text) return false;
    const known = soldOutThresholds();
    if (known.length) {
      for (let i = 0; i < known.length; i++) {
        if (text === known[i] || text.indexOf(known[i]) !== -1) return true;
      }
      return false;
    }
    // No rules to read — fall back to the app's rendered wording. Never assume
    // "available" from an absent status.
    return SOLD_OUT_FALLBACK.test(text);
  }

  function classify() {
    const drawer = document.querySelector('.iia-drawer');
    if (!drawer) return;
    const rows = drawer.querySelectorAll('.iia-list-item');
    for (let i = 0; i < rows.length; i++) {
      rows[i].classList.toggle(SOLD_OUT_CLASS, isSoldOut(rows[i]));
    }
  }

  let scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    raf(function () {
      scheduled = false;
      classify();
    });
  }

  /* True when a mutation touches the Find in Store drawer — the app rebuilds the
     drawer (new elements, not just new children) on open, variant change and geo
     search, so we watch the document and re-classify only for drawer-related
     changes. Handles element and text-node mutation targets, and freshly-added
     drawer/list nodes. */
  function touchesDrawer(mutations) {
    for (let i = 0; i < mutations.length; i++) {
      const m = mutations[i];
      const node = m.target;
      const el = node && (node.nodeType === 1 ? node : node.parentElement);
      if (el && el.closest && el.closest('.iia-drawer')) return true;
      const added = m.addedNodes;
      for (let j = 0; added && j < added.length; j++) {
        const n = added[j];
        if (
          n.nodeType === 1 &&
          n.matches &&
          (n.matches('.iia-drawer, .iia-list-item') ||
            (n.querySelector && n.querySelector('.iia-drawer, .iia-list-item')))
        ) {
          return true;
        }
      }
    }
    return false;
  }

  function init() {
    // The app re-renders the drawer's own status text on variant change, so
    // watch text changes too. Our class toggle is an attribute change and is not
    // observed, so it cannot re-trigger this (no loop). The observer is
    // persistent — the drawer element is replaced wholesale on variant change,
    // so a one-shot/drawer-scoped observer would go dead after the first render.
    const observer = new MutationObserver(function (mutations) {
      if (touchesDrawer(mutations)) schedule();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });

    // Belt-and-braces triggers.
    document.addEventListener('variant:change', schedule);
    document.addEventListener(
      'change',
      function (event) {
        const t = event.target;
        if (t && t.matches && t.matches('input[name="id"], select[name="id"]')) {
          schedule();
        }
      },
      true
    );
    document.addEventListener('click', function (event) {
      const t = event.target;
      if (t && t.closest && t.closest('.iia-drawer-link')) {
        setTimeout(schedule, 400);
      }
    });

    classify();
  }

  // Test surface — the browser wiring above consumes the same functions.
  window.JagFindInStoreLabels = {
    SOLD_OUT_CLASS: SOLD_OUT_CLASS,
    soldOutThresholds: soldOutThresholds,
    isSoldOut: isSoldOut,
    classify: classify
  };

  if (!window.__jagFisStatusLabelInit) {
    window.__jagFisStatusLabelInit = true;
    if (document.body) {
      init();
    } else {
      document.addEventListener('DOMContentLoaded', init);
    }
  }
})();
